import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Truck, Globe, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import { companyInfo, heroImages } from '../data/mock';
import lepusTruck from '../assets/images/lepus-group-truck.jpeg';

// Clean animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const CoverageArea = ({ area, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      variants={fadeIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay: index * 0.1 }}
      className="h-full"
    >
      <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#1d526d]/20 h-full flex flex-col group hover:-translate-y-1">
        <div className="w-16 h-16 bg-gradient-to-br from-[#1d526d] to-[#2a6b89] rounded-xl flex items-center justify-center mb-6 shadow-lg">
          <MapPin className="w-8 h-8 text-white" />
        </div>
        
        <h3 className="text-2xl font-bold text-black mb-4 group-hover:text-[#1d526d] transition-colors duration-300">
          {area.title}
        </h3>
        
        <p className="text-gray-600 mb-6 leading-relaxed text-lg flex-grow">
          {area.description}
        </p>
        
        <div className="space-y-3 mb-6">
          {area.routes.map((route, idx) => (
            <div key={idx} className="flex items-start text-gray-700">
              <CheckCircle className="w-5 h-5 text-[#1d526d] mr-3 flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed font-medium">{route}</span>
            </div>
          ))}
        </div>
        
        <div className="mt-auto pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 font-medium">Service Type:</span>
            <span className="font-bold text-[#1d526d]">{area.serviceType}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Coverage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const coverageAreas = [
    {
      title: "Ontario & Western Canada",
      description: "Regular routes serving Ontario and Western Canada with our premium team service, providing some of the quickest transit times in the industry.",
      routes: [
        "Ontario to British Columbia",
        "Ontario to Alberta",
        "Ontario to Saskatchewan",
        "Ontario to Manitoba"
      ],
      serviceType: "Team Service"
    },
    {
      title: "Midwest USA",
      description: "Dedicated service to the Midwest United States with regular scheduled departures and reliable transit times.",
      routes: [
        "Ontario to Chicago",
        "Ontario to Detroit",
        "Ontario to Minneapolis",
        "Ontario to Cleveland"
      ],
      serviceType: "FTL & LTL"
    },
    {
      title: "Northeastern USA",
      description: "Comprehensive coverage of the Northeastern United States with our reliable and timely deliveries.",
      routes: [
        "Ontario to New York",
        "Ontario to Boston",
        "Ontario to Philadelphia",
        "Ontario to Washington DC"
      ],
      serviceType: "Express Service"
    },
    {
      title: "Cross-Border Solutions",
      description: "Expert cross-border transportation services between Canada and the USA, handling all customs and regulatory requirements.",
      routes: [
        "Canada to USA corridors",
        "USA to Canada return loads",
        "Bonded carrier services",
        "Customs clearance support"
      ],
      serviceType: "Cross-Border"
    }
  ];

  const features = [
    {
      icon: Clock,
      title: "24/7 Operations",
      description: "Round-the-clock dispatch and customer support to keep your freight moving"
    },
    {
      icon: Truck,
      title: "Team Drivers",
      description: "Experienced team drivers for nonstop service and faster delivery times"
    },
    {
      icon: Globe,
      title: "Cross-Border Expertise",
      description: "Seamless movement between Canada and USA with customs expertise"
    },
    {
      icon: CheckCircle,
      title: "98.5% On-Time",
      description: "Industry-leading on-time service rate with damage-free deliveries"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Clean & Modern */}
      <section className="relative min-h-screen bg-gradient-to-br from-gray-50 to-white overflow-hidden pt-4 sm:pt-6 lg:pt-8">

        {/* Background Image */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src={lepusTruck}
            alt="Transportation Coverage Map" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/95"></div>
        </div>

        {/* Clean Geometric Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#1d526d]/5 to-transparent rounded-full blur-2xl"
          />
        </div>

        <div ref={heroRef} className="relative z-10 min-h-screen flex items-center py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <div className="text-center">
              <motion.h1
                variants={slideIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-5xl lg:text-6xl font-bold text-black mb-8"
              >
                Our
                <span className="block text-[#1d526d] mt-2">Coverage</span>
              </motion.h1>
              
              <motion.p
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed"
              >
                Lepus Group provides point-to-point service between Canada and the USA. 
                Our business area includes regular routes and irregular lanes where needed.
              </motion.p>

              <motion.div
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="flex flex-col sm:flex-row gap-6 justify-center"
              >
                <motion.a 
                  href="#coverage-areas"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#1d526d] hover:bg-black text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl inline-flex items-center justify-center group"
                >
                  Explore Coverage 
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </motion.a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Features */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Why Choose Our Coverage?</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Strategic coverage areas with specialized services designed to meet your specific transportation needs.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="text-center"
              >
                <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-r from-[#1d526d] to-[#2a6b89] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <feature.icon className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                </div>
                <h3 className="text-lg lg:text-xl font-bold text-black mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section id="coverage-areas" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Service Areas</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our comprehensive coverage ensures your freight reaches its destination efficiently, 
              whether it's regular routes or irregular lanes.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.1 }}
            className="grid md:grid-cols-2 gap-8"
          >
            {coverageAreas.map((area, index) => (
              <CoverageArea key={index} area={area} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Service Commitment */}
      <section className="py-16 lg:py-24 bg-[#1d526d] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={lepusTruck}
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Ship Across North America?</h2>
            <p className="text-xl text-gray-200 mb-12 leading-relaxed max-w-4xl mx-auto">
              With our extensive coverage and professional team, we're ready to handle your next shipment 
              across our comprehensive service network.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/contact"
                  className="bg-white text-[#1d526d] px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center justify-center group shadow-2xl"
                >
                  Get Coverage Quote 
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/contact"
                  className="border-2 border-white text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-[#1d526d] transition-all duration-300 flex items-center justify-center"
                >
                  Contact Us
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Coverage;